import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import creditsService from '../services/creditsService';
import { toast } from 'react-toastify';

const CreditsContext = createContext();

export const useCredits = () => {
  return useContext(CreditsContext);
};

export const CreditsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const userId = user?.id ?? null;
  const [creditBalance, setCreditBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const hasInitialLoadRef = useRef(false);

  const fetchCreditBalance = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setCreditBalance(0);
      setTransactions([]);
      hasInitialLoadRef.current = false;
      setHasInitialLoad(false);
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await creditsService.getCreditBalance();

      if (response.success) {
        setCreditBalance(response.current_balance || 0);
        setTransactions(response.recent_transactions || []);
        hasInitialLoadRef.current = true;
        setHasInitialLoad(true);
      } else {
        console.error('Failed to fetch credit balance:', response.error);
        setError(response.error || 'Failed to fetch credit balance');
        if (!hasInitialLoadRef.current) {
          setCreditBalance(0);
          setTransactions([]);
        }
      }
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      setError(error.message);
      if (!hasInitialLoadRef.current) {
        setCreditBalance(0);
        setTransactions([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, userId]);

  // Fetch credit balance when user is authenticated
  useEffect(() => {
    if (isAuthenticated && userId) {
      fetchCreditBalance();
    } else {
      // Reset credits when user logs out
      setCreditBalance(0);
      setTransactions([]);
      hasInitialLoadRef.current = false;
      setHasInitialLoad(false);
      setIsLoading(false);
    }
  }, [isAuthenticated, userId, fetchCreditBalance]);

  /**
   * Add credits to user account
   * @param {number} amount - Amount of credits to add
   * @returns {Promise<boolean>} Success status
   */
  const addCredits = async (amount) => {
    try {
      setError('');
      
      const response = await creditsService.addCredits(amount);
      
      // Handle the actual API response structure
      if (response.success) {
        // Update local state immediately with the new balance from API
        const newBalance = response.new_balance || (creditBalance + amount);
        setCreditBalance(newBalance);
        
        // Show success toast with actual values from API
        toast.success(
          response.message || 
          `Successfully added ${response.credits_added || amount} credits! New balance: ${newBalance}`
        );
        
        // Refresh full balance data to get updated transactions after a delay
        setTimeout(() => {
          fetchCreditBalance();
        }, 1000);
        
        return true;
      } else {
        const errorMsg = response.error || 'Failed to add credits';
        setError(errorMsg);
        toast.error(errorMsg);
        return false;
      }
    } catch (error) {
      console.error('Error adding credits:', error);
      const errorMsg = error.message || 'Failed to add credits';
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }
  };

  /**
   * Deduct credits from user account (for purchases)
   * @param {number} amount - Amount of credits to deduct
   * @returns {boolean} Success status
   */
  const deductCredits = (amount) => {
    if (creditBalance >= amount) {
      const newBalance = creditBalance - amount;
      setCreditBalance(newBalance);
      
      // Refresh balance from server after a short delay
      setTimeout(() => {
        fetchCreditBalance();
      }, 1500);
      
      return true;
    }
    return false;
  };

  /**
   * Check if user has sufficient credits
   * @param {number} requiredAmount - Required credit amount
   * @returns {boolean} Whether user has sufficient credits
   */
  const hasSufficientCredits = (requiredAmount) => {
    return creditBalance >= requiredAmount;
  };

  /**
   * Get credit statistics
   * @returns {Promise<Object>} Credit statistics
   */
  const getCreditStatistics = async () => {
    try {
      const response = await creditsService.getCreditStatistics();
      return response;
    } catch (error) {
      console.error('Error fetching credit statistics:', error);
      toast.error('Failed to fetch credit statistics');
      throw error;
    }
  };

  /**
   * Refresh credit balance from server (without showing loading state)
   */
  const refreshBalance = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await creditsService.getCreditBalance();
      
      if (response.success) {
        setCreditBalance(response.current_balance || 0);
        setTransactions(response.recent_transactions || []);
        setHasInitialLoad(true);
      }
    } catch (error) {
      console.error('Error refreshing balance:', error);
      // Don't show error for silent refresh
    }
  }, [isAuthenticated, user]);

  /**
   * Force update credit balance (for external updates)
   * @param {number} newBalance - New balance to set
   */
  const updateBalance = (newBalance) => {
    setCreditBalance(newBalance);
  };

  const value = {
    // State
    creditBalance,
    transactions,
    isLoading,
    error,
    hasInitialLoad,
    
    // Actions
    addCredits,
    deductCredits,
    hasSufficientCredits,
    getCreditStatistics,
    refreshBalance,
    fetchCreditBalance,
    updateBalance
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};